<template>
  <div class="min-h-screen flex flex-col">
    <AppHeader />
    
    <!-- 主视觉区域 -->
    <section class="relative h-96 flex items-center justify-center text-white bg-gradient-stone pt-16 overflow-hidden">
      <div class="absolute inset-0">
        <img :alt="$t('customerSupport.hero.title')" class="w-full h-full object-cover opacity-40"
             src="/images/customerSupport/hero-bg.jpg">
      </div>
      <div class="relative z-10 text-center px-4 max-w-4xl">
        <h1 class="text-5xl md:text-6xl font-extrabold leading-tight mb-4 animate-fade-in-up">
          {{ $t('customerSupport.hero.title') }}
        </h1>
        <p class="text-xl md:text-2xl mb-6 max-w-3xl mx-auto animate-fade-in-up delay-200">
          {{ $t('customerSupport.hero.subtitle') }}
        </p>
      </div>
    </section>

    <!-- 主要内容区域 -->
    <main class="flex-grow pt-16">
      <!-- 服务流程 -->
      <section class="py-20 px-6 md:px-12 bg-white">
        <h2 class="text-4xl font-extrabold text-center mb-14 text-gray-900">
          {{ $t('customerSupport.serviceProcess.title') }}
        </h2>
        <div class="max-w-7xl mx-auto">
          <div class="bg-gradient-to-br from-slate-50 via-white to-purple-50 rounded-[32px] p-6 md:p-8 xl:p-10 border border-purple-100 shadow-xl overflow-hidden">
            <div class="max-w-3xl mx-auto text-center mb-8">
              <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold mb-4">
                <i class="fas fa-route"></i>
                <span>{{ $t('common.standardizedDeliveryPath') }}</span>
              </div>
              <p class="text-lg text-gray-600 leading-relaxed">
                {{ $t('common.serviceProcessIntro') }}
              </p>
            </div>

            <div class="service-process-overview mb-8">
              <div
                v-for="step in processSteps"
                :key="`overview-${step.index}`"
                class="service-process-overview__item"
              >
                <div class="service-process-overview__index">{{ $t('common.step') }} {{ step.index }}</div>
                <div class="service-process-overview__title">{{ step.title }}</div>
              </div>
            </div>

            <div class="service-process-grid">
              <div
                v-for="step in processSteps"
                :key="step.index"
                class="service-process-card group"
              >
                <div class="flex items-start justify-between gap-4 mb-6">
                  <div :class="['w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-md flex-shrink-0', step.iconBg, step.iconColor]">
                    <i :class="step.icon"></i>
                  </div>

                  <div class="text-right flex-shrink-0">
                    <div class="text-xs font-semibold tracking-[0.2em] text-gray-400">{{ $t('common.step') }}</div>
                    <div class="text-3xl font-extrabold text-gray-200 leading-none">{{ step.index }}</div>
                  </div>
                </div>

                <h3 class="text-2xl font-bold mb-3 text-gray-900">{{ step.title }}</h3>
                <p class="text-gray-600 leading-7 flex-1">{{ step.description }}</p>

                <div class="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between">
                  <span class="text-sm text-gray-500">{{ $t('common.phase') }} {{ step.index }}</span>
                  <span class="text-purple-600 group-hover:translate-x-1 transition-transform">
                    <i class="fas fa-arrow-right"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 帮助中心 -->
      <section class="py-20 px-6 md:px-12 bg-gray-50">
        <h2 class="text-4xl font-extrabold text-center mb-14 text-gray-900">
          {{ $t('customerSupport.helpCenter.title') }}
        </h2>
        
        <div class="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-10">
          <!-- 左侧分类导航 -->
          <div class="lg:col-span-1">
            <div class="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              <h3 class="text-xl font-bold mb-6 text-gray-900">{{ $t('customerSupport.helpCenter.categories.title') }}</h3>
              <ul class="space-y-3">
                <li v-for="category in faqCategories" :key="category.value">
                  <button 
                    @click="activeFaqCategory = category.value"
                    :class="[
                      'w-full text-left py-3 px-4 rounded-lg font-medium flex items-center justify-between transition-colors',
                      activeFaqCategory === category.value 
                        ? 'bg-purple-50 text-purple-700' 
                        : 'hover:bg-gray-100 text-gray-700'
                    ]">
                    <span>
                      <i :class="['mr-3', category.icon]"></i>{{ category.label }}
                    </span>
                    <i :class="[
                      'fas fa-chevron-down text-sm transition-opacity',
                      activeFaqCategory === category.value ? 'opacity-100' : 'opacity-0'
                    ]"></i>
                  </button>
                </li>
              </ul>
              
              <div class="mt-8 pt-6 border-t border-gray-200">
                <h4 class="text-lg font-semibold mb-4 text-gray-900">{{ $t('customerSupport.helpCenter.needMoreHelp') }}</h4>
                <router-link
                  to="/contact"
                  class="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                >
                  <i class="fas fa-paper-plane mr-2"></i> {{ $t('nav.contact') }}
                </router-link>
              </div>
            </div>
          </div>
          
          <!-- 右侧问题列表 -->
          <div class="lg:col-span-3">
            <div class="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div class="relative mb-6">
                <input 
                  v-model="faqSearch"
                  type="text" 
                  :placeholder="$t('customerSupport.helpCenter.searchPlaceholder')" 
                  class="w-full py-3 pl-10 pr-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
              
              <!-- 服务咨询问题 -->
              <div v-show="activeFaqCategory === 'service'">
                <h3 class="text-2xl font-bold mb-6 text-gray-900">{{ $t('customerSupport.helpCenter.faq.service.title') }}</h3>
                
                <div class="space-y-6">
                  <div v-for="faq in serviceFaqs.value" :key="faq.id" class="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                    <button 
                      @click="toggleFaq(faq.id)"
                      class="w-full text-left flex items-center justify-between focus:outline-none">
                      <span class="text-lg font-semibold text-gray-900">{{ faq.question }}</span>
                      <i :class="[
                        'fas text-purple-600 transition-transform',
                        expandedFaqs.includes(faq.id) ? 'fa-minus' : 'fa-plus'
                      ]"></i>
                    </button>
                    <div v-show="expandedFaqs.includes(faq.id)" class="mt-3 text-gray-700 leading-relaxed" v-html="faq.answer"></div>
                  </div>
                </div>
              </div>
              
              <!-- 其他分类内容 -->
              <div v-show="activeFaqCategory === 'trouble'">
                <h3 class="text-2xl font-bold mb-6 text-gray-900">{{ $t('customerSupport.helpCenter.faq.trouble.title') }}</h3>
                <p class="text-gray-700">{{ $t('customerSupport.helpCenter.faq.trouble.content') }}</p>
              </div>
              
              <div v-show="activeFaqCategory === 'compliance'">
                <h3 class="text-2xl font-bold mb-6 text-gray-900">{{ $t('customerSupport.helpCenter.faq.compliance.title') }}</h3>
                <p class="text-gray-700">{{ $t('customerSupport.helpCenter.faq.compliance.content') }}</p>
              </div>
              
              <div v-show="activeFaqCategory === 'technical'">
                <h3 class="text-2xl font-bold mb-6 text-gray-900">{{ $t('customerSupport.helpCenter.faq.technical.title') }}</h3>
                <p class="text-gray-700">{{ $t('customerSupport.helpCenter.faq.technical.content') }}</p>
              </div>
              
              <div v-show="activeFaqCategory === 'account'">
                <h3 class="text-2xl font-bold mb-6 text-gray-900">{{ $t('customerSupport.helpCenter.faq.account.title') }}</h3>
                <p class="text-gray-700">{{ $t('customerSupport.helpCenter.faq.account.content') }}</p>
              </div>
            </div>
            
            <!-- 联系支持卡片 -->
            <div class="bg-gradient-stone rounded-xl shadow-lg p-8 text-white">
              <h3 class="text-2xl font-bold mb-4">{{ $t('customerSupport.helpCenter.instantHelp.title') }}</h3>
              <p class="mb-6 opacity-90">{{ $t('customerSupport.helpCenter.instantHelp.subtitle') }}</p>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div class="text-center">
                  <div class="text-4xl mb-2"><i class="fas fa-phone-alt"></i></div>
                  <p class="text-sm opacity-80 mb-1">{{ $t('customerSupport.helpCenter.instantHelp.hotline') }}</p>
                  <p class="font-bold">{{ $t('customerSupport.helpCenter.instantHelp.hotlineValue') }}</p>
                </div>
                <div class="text-center">
                  <div class="text-4xl mb-2"><i class="fas fa-envelope"></i></div>
                  <p class="text-sm opacity-80 mb-1">{{ $t('customerSupport.helpCenter.instantHelp.email') }}</p>
                  <p class="font-bold">{{ supportEmail }}</p>
                </div>
                <div class="text-center">
                  <div class="text-4xl mb-2"><i class="fas fa-clock"></i></div>
                  <p class="text-sm opacity-80 mb-1">{{ $t('customerSupport.helpCenter.instantHelp.serviceTime') }}</p>
                  <p class="font-bold">{{ $t('customerSupport.helpCenter.instantHelp.serviceTime24x7') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 服务级别协议 -->
      <section class="py-20 px-6 md:px-12 bg-white">
        <h2 class="text-4xl font-extrabold text-center mb-14 text-gray-900">
          {{ $t('customerSupport.sla.title') }}
        </h2>
        
        <div class="max-w-7xl mx-auto">
          <div class="bg-gray-50 rounded-xl p-8 mb-10">
            <p class="text-center text-gray-700 max-w-3xl mx-auto mb-8">
              {{ $t('customerSupport.sla.description') }}
            </p>
            
            <div class="overflow-x-auto">
              <table class="w-full min-w-[800px] bg-white rounded-lg shadow overflow-hidden">
                <thead class="bg-gray-100 text-left">
                  <tr>
                    <th class="py-4 px-6 font-semibold text-gray-900">{{ $t('customerSupport.sla.table.serviceItem') }}</th>
                    <th class="py-4 px-6 font-semibold text-green-700">{{ $t('customerSupport.sla.table.enterprise') }}</th>
                    <th class="py-4 px-6 font-semibold text-blue-700">{{ $t('customerSupport.sla.table.professional') }}</th>
                    <th class="py-4 px-6 font-semibold text-orange-700">{{ $t('customerSupport.sla.table.standard') }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr>
                    <td class="py-4 px-6 font-medium text-gray-900">{{ $t('customerSupport.sla.table.responseTime') }}</td>
                    <td class="py-4 px-6 text-green-700">{{ $t('customerSupport.sla.table.values.responseTime.enterprise') }}</td>
                    <td class="py-4 px-6 text-blue-700">{{ $t('customerSupport.sla.table.values.responseTime.professional') }}</td>
                    <td class="py-4 px-6 text-orange-700">{{ $t('customerSupport.sla.table.values.responseTime.standard') }}</td>
                  </tr>
                  <tr>
                    <td class="py-4 px-6 font-medium text-gray-900">{{ $t('customerSupport.sla.table.criticalResolution') }}</td>
                    <td class="py-4 px-6 text-green-700">{{ $t('customerSupport.sla.table.values.criticalResolution.enterprise') }}</td>
                    <td class="py-4 px-6 text-blue-700">{{ $t('customerSupport.sla.table.values.criticalResolution.professional') }}</td>
                    <td class="py-4 px-6 text-orange-700">{{ $t('customerSupport.sla.table.values.criticalResolution.standard') }}</td>
                  </tr>
                  <tr>
                    <td class="py-4 px-6 font-medium text-gray-900">{{ $t('customerSupport.sla.table.generalResolution') }}</td>
                    <td class="py-4 px-6 text-green-700">{{ $t('customerSupport.sla.table.values.generalResolution.enterprise') }}</td>
                    <td class="py-4 px-6 text-blue-700">{{ $t('customerSupport.sla.table.values.generalResolution.professional') }}</td>
                    <td class="py-4 px-6 text-orange-700">{{ $t('customerSupport.sla.table.values.generalResolution.standard') }}</td>
                  </tr>
                  <tr>
                    <td class="py-4 px-6 font-medium text-gray-900">{{ $t('customerSupport.sla.table.availability') }}</td>
                    <td class="py-4 px-6 text-green-700">{{ $t('customerSupport.sla.table.values.availability.enterprise') }}</td>
                    <td class="py-4 px-6 text-blue-700">{{ $t('customerSupport.sla.table.values.availability.professional') }}</td>
                    <td class="py-4 px-6 text-orange-700">{{ $t('customerSupport.sla.table.values.availability.standard') }}</td>
                  </tr>
                  <tr>
                    <td class="py-4 px-6 font-medium text-gray-900">{{ $t('customerSupport.sla.table.supportMethod') }}</td>
                    <td class="py-4 px-6 text-green-700">{{ $t('customerSupport.sla.table.supportMethod_enterprise') }}</td>
                    <td class="py-4 px-6 text-blue-700">{{ $t('customerSupport.sla.table.supportMethod_professional') }}</td>
                    <td class="py-4 px-6 text-orange-700">{{ $t('customerSupport.sla.table.supportMethod_standard') }}</td>
                  </tr>
                  <tr>
                    <td class="py-4 px-6 font-medium text-gray-900">{{ $t('customerSupport.sla.table.accountManager') }}</td>
                    <td class="py-4 px-6 text-green-700">√</td>
                    <td class="py-4 px-6 text-blue-700">√</td>
                    <td class="py-4 px-6 text-orange-700">×</td>
                  </tr>
                  <tr>
                    <td class="py-4 px-6 font-medium text-gray-900">{{ $t('customerSupport.sla.table.compensation') }}</td>
                    <td class="py-4 px-6 text-green-700">{{ $t('customerSupport.sla.table.compensation_enterprise') }}</td>
                    <td class="py-4 px-6 text-blue-700">{{ $t('customerSupport.sla.table.compensation_professional') }}</td>
                    <td class="py-4 px-6 text-orange-700">{{ $t('customerSupport.sla.table.compensation_standard') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div class="bg-white rounded-xl shadow-lg p-6 border-t-4 border-green-500">
              <h3 class="text-xl font-bold mb-4 text-gray-900">{{ $t('customerSupport.sla.levels.enterprise.title') }}</h3>
              <p class="text-gray-600 mb-6">{{ $t('customerSupport.sla.levels.enterprise.description') }}</p>
              <ul class="space-y-3 mb-6">
                <li v-for="(feature, index) in $tm('customerSupport.sla.levels.enterprise.features')" :key="index" class="flex items-start">
                  <i class="fas fa-check text-green-500 mt-1 mr-3"></i>
                  <span class="text-gray-700">{{ feature }}</span>
                </li>
              </ul>
              <a href="javascript:void(0);" class="inline-block text-green-600 hover:text-green-700 font-medium flex items-center">
                {{ $t('customerSupport.sla.levels.enterprise.learnMore') }} <i class="fas fa-arrow-right ml-2 text-sm"></i>
              </a>
            </div>
            
            <div class="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-500">
              <h3 class="text-xl font-bold mb-4 text-gray-900">{{ $t('customerSupport.sla.levels.professional.title') }}</h3>
              <p class="text-gray-600 mb-6">{{ $t('customerSupport.sla.levels.professional.description') }}</p>
              <ul class="space-y-3 mb-6">
                <li v-for="(feature, index) in $tm('customerSupport.sla.levels.professional.features')" :key="index" class="flex items-start">
                  <i class="fas fa-check text-blue-500 mt-1 mr-3"></i>
                  <span class="text-gray-700">{{ feature }}</span>
                </li>
              </ul>
              <a href="javascript:void(0);" class="inline-block text-blue-600 hover:text-blue-700 font-medium flex items-center">
                {{ $t('customerSupport.sla.levels.professional.learnMore') }} <i class="fas fa-arrow-right ml-2 text-sm"></i>
              </a>
            </div>
            
            <div class="bg-white rounded-xl shadow-lg p-6 border-t-4 border-orange-500">
              <h3 class="text-xl font-bold mb-4 text-gray-900">{{ $t('customerSupport.sla.levels.standard.title') }}</h3>
              <p class="text-gray-600 mb-6">{{ $t('customerSupport.sla.levels.standard.description') }}</p>
              <ul class="space-y-3 mb-6">
                <li v-for="(feature, index) in $tm('customerSupport.sla.levels.standard.features')" :key="index" class="flex items-start">
                  <i class="fas fa-check text-orange-500 mt-1 mr-3"></i>
                  <span class="text-gray-700">{{ feature }}</span>
                </li>
              </ul>
              <a href="javascript:void(0);" class="inline-block text-orange-600 hover:text-orange-700 font-medium flex items-center">
                {{ $t('customerSupport.sla.levels.standard.learnMore') }} <i class="fas fa-arrow-right ml-2 text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <!-- 售后反馈 -->
      <section class="py-20 px-6 md:px-12 bg-gray-50">
        <h2 class="text-4xl font-extrabold text-center mb-14 text-gray-900">
          {{ $t('customerSupport.feedback.title') }}
        </h2>
        <p class="max-w-3xl mx-auto text-center text-lg text-gray-600 mb-10">
          {{ $t('customerSupport.feedback.subtitle') }}
        </p>
        
        <div class="max-w-5xl mx-auto">
          <div class="bg-white rounded-xl shadow-lg p-8">
            <h3 class="text-2xl font-bold mb-6 text-gray-900">{{ $t('customerSupport.feedback.complaint.title') }}</h3>
            <p class="text-gray-600 mb-8">{{ $t('customerSupport.feedback.complaint.subtitle') }}</p>
            
            <div class="space-y-8">
              <div class="flex">
                <div class="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 mr-4">
                  <i class="fas fa-phone-alt text-xl"></i>
                </div>
                <div>
                  <h4 class="text-lg font-semibold mb-1 text-gray-900">{{ $t('customerSupport.feedback.complaint.hotline.title') }}</h4>
                  <p class="text-gray-600 mb-2">{{ $t('customerSupport.feedback.complaint.hotline.phone') }}</p>
                  <p class="text-sm text-gray-500">{{ $t('customerSupport.feedback.complaint.hotline.time') }}</p>
                </div>
              </div>
              
              <div class="flex">
                <div class="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-4">
                  <i class="fas fa-envelope text-xl"></i>
                </div>
                <div>
                  <h4 class="text-lg font-semibold mb-1 text-gray-900">{{ $t('customerSupport.feedback.complaint.email.title') }}</h4>
                  <p class="text-gray-600 mb-2">{{ complaintEmail }}</p>
                  <p class="text-sm text-gray-500">{{ $t('customerSupport.feedback.complaint.email.response') }}</p>
                </div>
              </div>
              
              <div class="flex">
                <div class="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 mr-4">
                  <i class="fas fa-comment-alt text-xl"></i>
                </div>
                <div>
                  <h4 class="text-lg font-semibold mb-1 text-gray-900">{{ $t('customerSupport.feedback.complaint.online.title') }}</h4>
                  <p class="text-gray-600 mb-2">{{ $t('customerSupport.feedback.complaint.online.description') }}</p>
                  <p class="text-sm text-gray-500">{{ $t('customerSupport.feedback.complaint.online.tracking') }}</p>
                </div>
              </div>
              
              <div class="flex">
                <div class="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 mr-4">
                  <i class="fas fa-file-alt text-xl"></i>
                </div>
                <div>
                  <h4 class="text-lg font-semibold mb-1 text-gray-900">{{ $t('customerSupport.feedback.complaint.process.title') }}</h4>
                  <ol class="list-decimal pl-5 text-gray-600 space-y-1 mt-2">
                    <li v-for="(step, index) in $tm('customerSupport.feedback.complaint.process.steps')" :key="index">{{ step }}</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 联系我们CTA -->
      <section class="py-20 px-6 md:px-12 bg-gradient-stone text-white">
        <div class="max-w-5xl mx-auto text-center">
          <h2 class="text-4xl font-extrabold mb-8">
            {{ $t('customerSupport.cta.title') }}
          </h2>
          <p class="text-xl leading-relaxed mb-12 opacity-90">
            {{ $t('customerSupport.cta.subtitle') }}
          </p>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
            <router-link
              to="/contact"
              class="inline-block bg-white text-purple-700 hover:bg-purple-100 px-8 py-4 rounded-full text-lg font-semibold shadow-lg transition duration-300 transform hover:scale-105">
              {{ $t('customerSupport.cta.contactSupport') }}
              <i class="fas fa-paper-plane ml-2"></i>
            </router-link>
            <a class="inline-block bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg font-semibold shadow-lg transition duration-300" href="javascript:void(0);">
              {{ $t('customerSupport.cta.viewDocumentation') }}
              <i class="fas fa-book ml-2"></i>
            </a>
          </div>
        </div>
      </section>
    </main>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';

const { t, tm } = useI18n();
const CONTACT_EMAIL = 'info@global-data.com';

// FAQ 分类 - 使用computed从翻译获取
const faqCategories = computed(() => [
  { value: 'service', label: t('customerSupport.helpCenter.categories.service'), icon: 'fas fa-question-circle' },
  { value: 'trouble', label: t('customerSupport.helpCenter.categories.trouble'), icon: 'fas fa-exclamation-triangle' },
  { value: 'compliance', label: t('customerSupport.helpCenter.categories.compliance'), icon: 'fas fa-check-circle' },
  { value: 'technical', label: t('customerSupport.helpCenter.categories.technical'), icon: 'fas fa-code' },
  { value: 'account', label: t('customerSupport.helpCenter.categories.account'), icon: 'fas fa-user' }
]);

const processSteps = computed(() => [
  {
    index: '01',
    title: t('customerSupport.serviceProcess.steps.step1.title'),
    description: t('customerSupport.serviceProcess.steps.step1.description'),
    icon: 'fas fa-handshake',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600'
  },
  {
    index: '02',
    title: t('customerSupport.serviceProcess.steps.step2.title'),
    description: t('customerSupport.serviceProcess.steps.step2.description'),
    icon: 'fas fa-project-diagram',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },
  {
    index: '03',
    title: t('customerSupport.serviceProcess.steps.step3.title'),
    description: t('customerSupport.serviceProcess.steps.step3.description'),
    icon: 'fas fa-rocket',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600'
  },
  {
    index: '04',
    title: t('customerSupport.serviceProcess.steps.step4.title'),
    description: t('customerSupport.serviceProcess.steps.step4.description'),
    icon: 'fas fa-tools',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600'
  },
  {
    index: '05',
    title: t('customerSupport.serviceProcess.steps.step5.title'),
    description: t('customerSupport.serviceProcess.steps.step5.description'),
    icon: 'fas fa-chart-line',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600'
  }
]);

const activeFaqCategory = ref('service');
const faqSearch = ref('');
const expandedFaqs = ref([]);

const supportEmail = computed(() => CONTACT_EMAIL);
const complaintEmail = computed(() => CONTACT_EMAIL);
const serviceInquiryEmail = computed(() => CONTACT_EMAIL);

// 服务咨询FAQ数据 - 使用computed从翻译获取
const serviceFaqs = computed(() => {
  const q1 = tm('customerSupport.helpCenter.faq.service.q1');
  const q2 = tm('customerSupport.helpCenter.faq.service.q2');
  const q3 = tm('customerSupport.helpCenter.faq.service.q3');
  
  const formatAnswer = (answerData, listType = 'ul') => {
    if (!answerData || !answerData.intro) return '';
    
    const listItems = answerData.list
      .map(item => item.replaceAll('{serviceEmail}', serviceInquiryEmail.value))
      .map(item => `<li>${item}</li>`)
      .join('');
    
    return `
      <p>${answerData.intro}</p>
      <${listType} class="list-${listType === 'ol' ? 'decimal' : 'disc'} pl-5 mt-2 space-y-1">
        ${listItems}
      </${listType}>
      <p class="mt-3">${answerData.conclusion}</p>
    `;
  };
  
  return [
    {
      id: 'faq1',
      question: q1.question,
      answer: formatAnswer(q1.answer)
    },
    {
      id: 'faq2',
      question: q2.question,
      answer: formatAnswer(q2.answer, 'ol')
    },
    {
      id: 'faq3',
      question: q3.question,
      answer: formatAnswer(q3.answer)
    }
  ];
});

// 切换FAQ展开/折叠
const toggleFaq = (faqId) => {
  const index = expandedFaqs.value.indexOf(faqId);
  if (index > -1) {
    expandedFaqs.value.splice(index, 1);
  } else {
    expandedFaqs.value.push(faqId);
  }
};
</script>

<style>
@import '@/assets/css/main.css';
@import '@/assets/css/customerSupportView.css';

.service-process-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 1.25rem;
}

.service-process-overview {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.service-process-overview__item {
  padding: 1rem 1.25rem;
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
  text-align: center;
}

.service-process-overview__index {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: #9ca3af;
  margin-bottom: 0.35rem;
}

.service-process-overview__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
}

.service-process-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 1.75rem;
  border-radius: 1.75rem;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.service-process-card:hover {
  transform: translateY(-6px);
  border-color: rgba(192, 132, 252, 0.6);
  box-shadow: 0 24px 50px rgba(124, 58, 237, 0.12);
}

@media (min-width: 768px) {
  .service-process-overview {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .service-process-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .service-process-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 1rem;
  }
}
</style>
